import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  Search,
  X,
  Check,
  Play,
  Pause,
  Loader2,
  ChevronDown,
} from "lucide-react";

const getCategoryLabel = (val) => {
  switch (val) {
    case "professional":
      return "Professional";
    case "famous":
      return "Famous";
    case "high_quality":
      return "High Quality";
    default:
      return "Category";
  }
};

const VoiceLibrarySidebar = ({
  showVoiceLibrary,
  setShowVoiceLibrary,
  selectedVoice,
  setSelectedVoice,
  voicesData,
  voiceSearchQuery,
  setVoiceSearchQuery,
  voiceFilterCategory = "all",
  setVoiceFilterCategory,
  voiceFilterGender,
  setVoiceFilterGender,
  voiceFilterAge,
  setVoiceFilterAge,
  loadMoreVoices,
  hasMoreVoices,
  isLoadingMore,
  loadingVoices,
  voicePage,
  previousVoiceId,
  prioritizedVoiceId,
}) => {
  // Audio playback states (keep these local to sidebar)
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);
  const audioRef = useRef(new Audio());

  // Search is local to the library experience. Reset it whenever the sidebar
  // closes so the compact voice row returns to the normal voice collection.
  useEffect(() => {
    if (showVoiceLibrary) return;

    audioRef.current.pause();
    setCurrentlyPlayingId(null);
    setIsPlaying(false);
    setIsLoading(false);

    if (voiceSearchQuery) {
      setVoiceSearchQuery("");
    }
  }, [showVoiceLibrary, voiceSearchQuery, setVoiceSearchQuery]);

  // Clear local loading state as soon as loading finishes
  useEffect(() => {
    if (!loadingVoices) {
      setIsFilterChanging(false);
    }
  }, [loadingVoices, voicesData]);

  // Ref for scroll container and intersection observer
  const scrollContainerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);

  // The API owns filtering. While browsing, keep the selected voice first;
  // during search, show only the API's matching results.
  const filteredVoices = useMemo(() => {
    const voices = voicesData || [];
    if (voiceSearchQuery || !prioritizedVoiceId) return voices;

    const selectedIndex = voices.findIndex(
      (voice) => voice.voiceId === prioritizedVoiceId
    );
    if (selectedIndex <= 0) return voices;

    return [
      voices[selectedIndex],
      ...voices.slice(0, selectedIndex),
      ...voices.slice(selectedIndex + 1),
    ];
  }, [voicesData, prioritizedVoiceId, voiceSearchQuery]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
      setIsLoading(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentlyPlayingId(null);
      setIsLoading(false);
    };

    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("canplay", handleCanPlay);

    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.pause();
    };
  }, []);

  // Play voice function
  const handlePlayVoice = (voiceId, previewUrl) => {
    if (!previewUrl) return;

    const audio = audioRef.current;

    if (currentlyPlayingId === voiceId && isPlaying) {
      // Pause if currently playing this voice
      audio.pause();
      setCurrentlyPlayingId(null);
      setIsLoading(false);
    } else {
      // Stop current audio
      if (currentlyPlayingId) {
        audio.pause();
      }

      // Set loading state
      setIsLoading(true);
      setCurrentlyPlayingId(voiceId);

      // Set new audio source
      audio.src = previewUrl;
      audio.load();

      // Play the audio
      audio.play().catch((error) => {
        console.error("Error playing audio:", error);
        setCurrentlyPlayingId(null);
        setIsLoading(false);
      });
    }
  };

  // The preview Audio instance is created once and never replaced.
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) audio.pause();
    };
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMoreVoices && !isLoadingMore) {
          loadMoreVoices();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: "100px", // Trigger 100px before reaching the bottom
        threshold: 0.1,
      }
    );

    const currentTrigger = loadMoreTriggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [hasMoreVoices, isLoadingMore, loadMoreVoices]);

  return (
    <div
      className={`fixed inset-y-0 right-0 box-border pt-14 w-full max-w-[500px] overflow-x-hidden bg-white dark:bg-[#111111]
        border-l border-gray-200 dark:border-gray-800 shadow-xl transform
        transition-transform duration-300 ease-in-out z-50 will-change-transform
        ${showVoiceLibrary ? "translate-x-0" : "translate-x-full"}`}
    >
      <div className="flex flex-col h-full">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-3 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Voice Library
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Choose from our collection of natural-sounding voices
            </p>
          </div>

          <button
            type="button"
            aria-label="Close voice library"
            onClick={() => setShowVoiceLibrary(false)}
            className="shrink-0 p-2 rounded-lg text-gray-500 hover:text-gray-700
            dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100
            dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* SEARCH + FILTERS */}
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-800 space-y-4 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search voices..."
              value={voiceSearchQuery}
              onChange={(e) => {
                setIsFilterChanging(true);
                setVoiceSearchQuery(e.target.value);
              }}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 
              dark:border-gray-700 bg-gray-50 dark:bg-[#161616] text-gray-900 
              dark:text-white focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* FILTER BUTTONS */}
          <div className="flex gap-2 flex-wrap">
            {/* Category */}
            <Menu as="div" className="relative">
              <Menu.Button
                className="gap-2 px-3 py-1.5 text-sm rounded-lg 
                border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161616] 
                text-gray-700 dark:text-gray-300 flex items-center"
              >
                {getCategoryLabel(voiceFilterCategory)}
                <ChevronDown className="h-3.5 w-3.5" />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Menu.Items
                  className="absolute left-0 mt-2 w-44 rounded-lg 
                  border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#111111] 
                  shadow-lg z-50"
                >
                  <div className="p-1">
                    {[
                      { label: "All Voices", value: "all" },
                      { label: "Professional", value: "professional" },
                      { label: "Famous", value: "famous" },
                      { label: "High Quality", value: "high_quality" },
                    ].map((cat) => (
                      <Menu.Item key={cat.value}>
                        {({ active }) => (
                          <button
                            onClick={() => {
                              setIsFilterChanging(true);
                              setVoiceFilterCategory && setVoiceFilterCategory(cat.value);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 ${
                              active ? "bg-gray-100 dark:bg-[#161616]" : ""
                            }`}
                          >
                            {cat.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            {/* Gender */}
            <Menu as="div" className="relative">
              <Menu.Button
                className="gap-2 px-3 py-1.5 text-sm rounded-lg 
                border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161616] 
                text-gray-700 dark:text-gray-300 flex items-center"
              >
                {voiceFilterGender === "all"
                  ? "Gender"
                  : voiceFilterGender.charAt(0).toUpperCase() +
                  voiceFilterGender.slice(1)}
                <ChevronDown className="h-3.5 w-3.5" />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Menu.Items
                  className="absolute right-0 mt-2 w-40 rounded-lg
                  border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#111111]
                  shadow-lg z-50"
                >
                  <div className="p-1">
                    {["all", "female", "male"].map((gender) => (
                      <Menu.Item key={gender}>
                        {({ active }) => (
                          <button
                            onClick={() => {
                              setIsFilterChanging(true);
                              setVoiceFilterGender(gender);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-gray-100 dark:bg-[#161616]" : ""
                              }`}
                          >
                            {gender === "all"
                              ? "All Genders"
                              : gender.charAt(0).toUpperCase() +
                              gender.slice(1)}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            {/* Age */}
            <Menu as="div" className="relative">
              <Menu.Button
                className="gap-2 px-3 py-1.5 text-sm rounded-lg 
                border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161616] 
                text-gray-700 dark:text-gray-300 flex items-center"
              >
                {voiceFilterAge === "all"
                  ? "Age"
                  : voiceFilterAge.charAt(0).toUpperCase() +
                  voiceFilterAge.slice(1)}
                <ChevronDown className="h-3.5 w-3.5" />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Menu.Items
                  className="absolute right-0 mt-2 w-40 rounded-lg
                  border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#111111]
                  shadow-lg z-50"
                >
                  <div className="p-1">
                    {["all", "young", "middle aged"].map((age) => (
                      <Menu.Item key={age}>
                        {({ active }) => (
                          <button
                            onClick={() => {
                              setIsFilterChanging(true);
                              setVoiceFilterAge(age);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-gray-100 dark:bg-[#161616]" : ""
                              }`}
                          >
                            {age === "all"
                              ? "All Ages"
                              : age.charAt(0).toUpperCase() + age.slice(1)}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>

        {/* VOICE LIST */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto min-h-0 overscroll-contain scroll-smooth relative"
        >
          <div className="p-4 sm:p-6 space-y-3">
            {/* Loading state for initial load or filter changes */}
            {(loadingVoices || isFilterChanging) && (voicePage === 1 || !voicePage) ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#161616] animate-pulse"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/3" />
                      <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-2/3" />
                    </div>
                    <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600" />
                  </div>
                ))}
              </div>
            ) : filteredVoices.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  No voices found matching your criteria.
                </p>
              </div>
            ) : (
              filteredVoices?.map((voice) => {
                const isPreviousVoice = previousVoiceId === voice.voiceId;

                return (
                  <div
                    key={voice.voiceId}
                    className={`flex min-w-0 items-center justify-between p-3 sm:p-4 rounded-lg border ${selectedVoice === voice.voiceId
                      ? "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700"
                      : "bg-gray-50 dark:bg-[#161616] border-gray-200 dark:border-gray-700"
                      }`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Play Button */}
                      <button
                        type="button"
                        onClick={() =>
                          handlePlayVoice(voice.voiceId, voice.previewUrl)
                        }
                        className="flex shrink-0 items-center justify-center w-10 h-10 rounded-full border-2
                      border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white
                      disabled:opacity-50"
                        disabled={
                          !voice.previewUrl ||
                          (isLoading && currentlyPlayingId === voice.voiceId)
                        }
                      >
                        {isLoading && currentlyPlayingId === voice.voiceId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : currentlyPlayingId === voice.voiceId && isPlaying ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </button>

                      {/* Voice Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 mb-1">
                          <span className="min-w-0 max-w-full font-medium text-sm text-gray-900 dark:text-white truncate">
                            {voice.name}
                          </span>

                          {isPreviousVoice && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs 
                          font-medium bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400"
                            >
                              Previous
                            </span>
                          )}

                          {selectedVoice === voice?.voiceId && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs 
                          font-medium bg-orange-500 text-white"
                            >
                              Selected
                            </span>
                          )}
                        </div>

                        <p className="text-xs mb-1 text-gray-600 dark:text-gray-400 capitalize">
                          {[
                            voice?.gender,
                            voice?.age?.replace(/_/g, " "),
                            voice?.accent,
                            voice?.category?.replace(/_/g, " "),
                          ]
                            .filter(Boolean)
                            .map((str) => str.charAt(0).toUpperCase() + str.slice(1))
                            .join(", ")}
                        </p>

                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          {voice.description}
                        </p>
                      </div>
                    </div>

                    {/* Select Button */}
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedVoice(voice.voiceId, voice, {
                          fromVoiceLibrary: true,
                        })
                      }
                      className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center ml-3 ${selectedVoice === voice.voiceId
                        ? "bg-orange-500 border-orange-500"
                        : "border-gray-300 dark:border-gray-600 hover:border-orange-400"
                        }`}
                    >
                      {selectedVoice === voice.voiceId && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </button>
                  </div>
                );
              })
            )}

            {/* Loading indicator for infinite scroll (page 2+) */}
            {loadingVoices && voicePage > 1 && (
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                <span className="sr-only">Loading more voices...</span>
              </div>
            )}

            {/* Trigger element for intersection observer */}
            {hasMoreVoices && !isLoadingMore && (
              <div ref={loadMoreTriggerRef} className="h-4" />
            )}

            {/* End of list message */}
            {!hasMoreVoices && filteredVoices.length > 0 && (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You've reached the end of the list
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceLibrarySidebar;
